import { useState } from "react";
import { motion } from "framer-motion";
import { Shirt, Music, UtensilsCrossed, Gamepad2, ShoppingBag, Camera, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DJ_IMG = "/moments/a1.webp";
const SHOP_IMG = "/moments/a24.webp";

const FEATURES = [
  { icon: Shirt, label: "One-of-a-kind fashion show" },
  { icon: Music, label: "Live DJ all night" },
  { icon: UtensilsCrossed, label: "Food & drinks" },
  { icon: Gamepad2, label: "Interactive games" },
  { icon: ShoppingBag, label: "Bougie-on-a-budget pop-up shop" },
  { icon: Camera, label: "Red carpet & photo ops" },
  { icon: Sparkles, label: "Surprise special guests" },
  { icon: Heart, label: "Lovely bear-hug activations" },
];

const TIMELINE = [
  { date: "08.19", text: "The movement begins — daily activations open" },
  { date: "10.01", text: "VIP Wave One — first invitations sent to the most consistent" },
  { date: "11.15", text: "VIP Wave Two — the final list is drawn" },
  { date: "12.19", text: "The Kindness Ball" },
];

export function Ball() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [guests, setGuests] = useState("1");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/rsvp", { name, email, guests: Number(guests) });
      toast.success("You're on the list. Now keep your streak alive — invitations are earned.");
      setName(""); setEmail(""); setGuests("1");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="ball" className="border-t border-[#33240E]/15 bg-[#FFF6DF]/70 py-32" data-testid="ball-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="mb-20 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.4em] text-[#8A5A0F]">Saturday, December 19, 2026</p>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.4em] text-[#33240E]/60" data-testid="ball-location">Location sent privately to VIP guests</p>
            <h2 className="font-serif text-4xl font-black text-[#33240E] sm:text-5xl lg:text-6xl">
              The Kindness Ball<span className="text-velvet">.</span><br />
              <span className="italic text-[#8A5A0F]">It's a whole vibe.</span>
            </h2>
          </div>
          <p className="max-w-md self-end text-base font-light leading-relaxed text-muted-foreground lg:col-span-4">
            One night where every checked-off day pays off. You can't buy your way onto this list —
            you be-kind your way onto it.
          </p>
        </div>

        <div className="mb-20 grid gap-6 lg:grid-cols-12">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:col-span-7">
            <img src={DJ_IMG} alt="The ballroom crowd under the chandeliers" className="aspect-[16/10] w-full object-cover saturate-[0.75]" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 lg:pt-24">
            <img src={SHOP_IMG} alt="A guest with her bougie-on-a-budget pop-up find" className="aspect-[4/5] w-full object-cover saturate-[0.85]" />
          </motion.div>
        </div>

        <div className="mb-24 grid grid-cols-2 gap-px bg-[#33240E]/10 lg:grid-cols-4" data-testid="ball-features">
          {FEATURES.map((f, i) => (
            <motion.div key={f.label} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
              className="group flex min-h-[140px] flex-col justify-between bg-[#FDF3D8] p-6 transition-colors duration-300 hover:bg-[#FFF9EA]">
              <f.icon className="h-5 w-5 text-[#8A5A0F] transition-transform duration-300 group-hover:scale-110" />
              <span className="text-sm font-light text-[#33240E]/80">{f.label}</span>
            </motion.div>
          ))}
        </div>

        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h3 className="mb-10 font-serif text-3xl font-bold text-[#33240E]">How you get in</h3>
            <div data-testid="vip-timeline">
              {TIMELINE.map((t, i) => (
                <motion.div key={t.date} initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }} className="flex gap-8 border-t border-[#33240E]/15 py-6">
                  <span className="font-mono text-sm text-[#8A5A0F]">{t.date}</span>
                  <span className="text-base font-light text-[#33240E]/80">{t.text}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <h3 className="mb-4 font-serif text-3xl font-bold text-[#33240E]">Get on the list</h3>
            <p className="mb-8 max-w-md text-sm font-light leading-relaxed text-muted-foreground">
              Join the interest list now. Invitations go to those who show up for the daily activations —
              the first VIP wave lands October 1.
            </p>
            <form onSubmit={submit} className="space-y-6" data-testid="rsvp-form">
              <div className="space-y-2">
                <Label htmlFor="rsvp-name" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input id="rsvp-name" data-testid="rsvp-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]" placeholder="Your full name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rsvp-email" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input id="rsvp-email" type="email" data-testid="rsvp-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]" placeholder="you@example.com" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Party size</Label>
                <Select value={guests} onValueChange={setGuests}>
                  <SelectTrigger data-testid="rsvp-guests-select" className="rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFF9EA] text-[#33240E]">
                    {["1", "2", "3", "4"].map((g) => <SelectItem key={g} value={g}>{g === "1" ? "Just me" : `Me + ${Number(g) - 1}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button type="submit" disabled={busy} data-testid="rsvp-submit-button"
                className="w-full bg-[#33240E] py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517] disabled:opacity-50">
                {busy ? "Saving..." : "Join the List"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
